import React from 'react';
import styled from '@emotion/styled';
import PatternBasedRecommendations from './PatternBasedRecommendations';

interface LeftSidebarProps {
  userId?: string;
  onExplore: (query: string) => void;
  trigger?: number;
  isOpen?: boolean;
  onClose?: () => void;
  onShowLeaderboard?: () => void;
  onNavigateToGraph?: () => void;
  onOpenRightSidebar?: () => void;
  hasHeaderSearch?: boolean;
}

const SidebarContainer = styled.div<{ isOpen?: boolean; hasHeaderSearch?: boolean }>`
  width: 280px;
  height: calc(100vh - 60px);
  background: rgba(var(--color-surface-rgb, 30, 41, 59), 0.85);
  backdrop-filter: blur(12px);
  border-right: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: fixed;
  left: 0;
  top: 60px;
  z-index: 20;
  
  @media (max-width: 1024px) {
    top: ${props => props.hasHeaderSearch ? '140px' : '60px'};
    height: ${props => props.hasHeaderSearch ? 'calc(100vh - 140px)' : 'calc(100vh - 60px)'};
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
  }
`;

const Section = styled.div`
  padding: var(--gap-lg);
  flex: 1;
  overflow-y: auto;
`;

const MobileNavSection = styled.div`
  display: none;
  
  @media (max-width: 1024px) {
    display: flex;
    justify-content: center;
    padding: var(--gap-lg);
    border-bottom: 1px solid var(--color-border);
    background: var(--color-background);
  }
`;

const MobileNavGrid = styled.div`
  display: flex;
  gap: var(--gap-sm);
`;

const MobileNavButton = styled.button`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--gap-md);
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  
  &:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-primary);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
    opacity: 0.8;
  }
`;

const LeftSidebar: React.FC<LeftSidebarProps> = ({ userId, onExplore, trigger, isOpen = false, onClose, onShowLeaderboard, onNavigateToGraph, onOpenRightSidebar, hasHeaderSearch = false }) => {
  return (
    <SidebarContainer isOpen={isOpen} hasHeaderSearch={hasHeaderSearch}>
      {/* Mobile Navigation Icons */}
      <MobileNavSection>
        <MobileNavGrid>
          <MobileNavButton onClick={() => { onOpenRightSidebar?.(); onClose?.(); }} title="Your Journey">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 115">
              <path d="m53.5 34.207c-1.168 0-2.082 0.91797-2.082 2.082v15.207c0 0.54297 0.20703 1.082 0.625 1.457l12.418 12.418c0.41797 0.41797 0.95703 0.625 1.457 0.625s1.082-0.20703 1.457-0.625c0.83203-0.83203 0.83203-2.125 0-2.957l-11.832-11.832v-14.289c0.039062-1.125-0.91797-2.0859-2.043-2.0859z"/>
              <path d="m53.375 15.875c-18.082 0-32.918 14.168-34 32l-3.25-3.707c-0.75-0.875-2.082-0.95703-2.957-0.20703s-0.95703 2.082-0.20703 2.957l6.957 8.043c0.41797 0.45703 0.95703 0.70703 1.582 0.70703s1.168-0.25 1.582-0.70703l6.957-8.043c0.75-0.875 0.66797-2.168-0.20703-2.957-0.875-0.75-2.168-0.66797-2.957 0.20703l-3.418 3.957c1.043-15.668 14.043-28.082 29.918-28.082 16.543 0 29.957 13.457 29.957 29.957s-13.457 29.957-29.957 29.957c-10.582 0-20.168-5.418-25.625-14.457-0.58203-1-1.875-1.293-2.875-0.70703-0.95703 0.58203-1.293 1.875-0.70703 2.875 6.25 10.293 17.168 16.457 29.207 16.457 18.832 0 34.125-15.293 34.125-34.125s-15.293-34.125-34.125-34.125z"/>
            </svg>
          </MobileNavButton>
          <MobileNavButton onClick={() => { onShowLeaderboard?.(); onClose?.(); }} title="Leaderboard">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
              <path d="m22.328 3.4102c-0.74609 0.003906-1.3672 0.5625-1.4531 1.3008-0.26562 2.4023-0.44531 4.7305-0.54688 6.9922h-15.914c-0.44922 0-0.875 0.20312-1.1562 0.55859s-0.38281 0.81641-0.27734 1.2578l5.0039 20.668c2.1328 8.8125 8.5938 15.941 17.152 18.934l7.2227 2.5273c4.4609 6.1602 9.957 11.516 16.176 16.371v5.875l-17.336 16.145v-0.003906c-0.44922 0.41016-0.59766 1.0547-0.375 1.6211 0.21875 0.5625 0.76562 0.93359 1.3711 0.93359h35.609c0.60547 0 1.1523-0.37109 1.3711-0.93359 0.22266-0.56641 0.074219-1.2109-0.375-1.6211l-17.324-16.133v-5.8867c6.2188-4.8594 11.719-10.215 16.176-16.375l7.207-2.5234c8.5625-2.9922 15.031-10.117 17.164-18.934l4.9922-20.668 0.003906 0.003906c0.10547-0.4375 0.003907-0.89844-0.27344-1.2539-0.27734-0.35156-0.69922-0.55859-1.1484-0.5625h-15.914c-0.10156-2.2617-0.28125-4.5898-0.54688-6.9922-0.085938-0.74219-0.71484-1.3047-1.4648-1.3008zm1.4648 2.9414h52.426c3.0352 31.043-8.1797 48.887-26.215 63.09-18.039-14.203-29.246-32.047-26.211-63.09zm-17.52 8.2969h13.973c-0.20312 15.059 3.3125 27.004 9.3984 36.926l-3.5234-1.2305c-7.6211-2.6641-13.375-9.0039-15.273-16.848zm73.5 0h13.961l-4.5703 18.844c-1.8984 7.8477-7.6406 14.188-15.266 16.848l-3.5234 1.2305c6.0859-9.9219 9.6016-21.867 9.3984-36.926zm-29.773 65.895 14.082 13.102h-28.152z"/>
            </svg>
          </MobileNavButton>
          <MobileNavButton onClick={() => { onNavigateToGraph?.(); onClose?.(); }} title="Knowledge Graph">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
              <path d="m89.34 62.84c-2.0508 0-3.8906 0.87109-5.1992 2.25l-18.551-11.25c0.57812-1.3398 0.91016-2.8086 0.91016-4.3594 0-3.4219-1.5703-6.4688-4.0195-8.4883l9.0703-14.801c1.0703 0.42969 2.2305 0.67969 3.4492 0.67969 5.0586 0 9.1914-4.1211 9.1914-9.1914 0-5.0703-4.1211-9.1914-9.1914-9.1914s-8.9492 3.8984-9.1602 8.7812c0 0.14062-0.019532 0.26953-0.019532 0.41016 0 0.21875 0.019532 0.42969 0.03125 0.64062l-10.828 2.5898c-0.98047-2.1484-2.8984-3.7695-5.2305-4.3594-0.60938-0.14844-1.25-0.23828-1.9102-0.23828-0.41016 0-0.82031 0.039062-1.2188 0.10156-2.5391 0.39844-4.6797 2.0117-5.7891 4.2305l-14.5-4.0391c0.050781-0.35938 0.078125-0.73047 0.10156-1.1016 0-0.17188 0.03125-0.33984 0.03125-0.51172 0-6.3398-5.1602-11.5-11.5-11.5-6.3477-0.003907-11.508 5.168-11.508 11.508s5.1602 11.5 11.5 11.5c2.9609 0 5.6602-1.1289 7.6992-2.9805l23.57 20.059c-0.75 1.1719-1.2812 2.4883-1.5391 3.8984l-15.109-0.69141c-0.55859-3.3906-3.5117-5.9805-7.0508-5.9805-3.9492 0-7.1602 3.2109-7.1602 7.1602 0 3.9492 3.2109 7.1602 7.1602 7.1602 3.3203 0 6.1094-2.2695 6.9102-5.3398l15.109 0.69141c0.23047 2.5508 1.3398 4.8516 3.0195 6.6016l-10.43 13.211c-2.1914-1.3906-4.7812-2.1992-7.5586-2.1992-7.8281 0-14.211 6.3711-14.211 14.211 0 7.8398 6.3711 14.211 14.211 14.211s14.211-6.3711 14.211-14.211c0-3.9688-1.6406-7.5703-4.2812-10.148l10.43-13.211c1.6289 0.96094 3.5312 1.5312 5.5586 1.5312 0.75 0 1.4883-0.078125 2.1992-0.21875l3.9492 11.23c-1.5898 1.0703-2.6406 2.8906-2.6406 4.9492 0 3.2891 2.6797 5.9688 5.9688 5.9688s5.9688-2.6797 5.9688-5.9688c0-3.2891-2.6797-5.9688-5.9688-5.9688-0.17188 0-0.33984 0.011718-0.5 0.03125l-3.9609-11.238c1.3516-0.69922 2.5391-1.6602 3.4883-2.8281l18.551 11.25c-0.26172 0.73828-0.39844 1.5195-0.39844 2.3398 0 3.9492 3.2109 7.1602 7.1602 7.1602s7.1602-3.2109 7.1602-7.1602-3.2109-7.1602-7.1602-7.1602zm-4.1602 7.1602c0-2.2891 1.8594-4.1602 4.1602-4.1602 2.3008 0 4.1602 1.8594 4.1602 4.1602s-1.8711 4.1602-4.1602 4.1602c-2.2891 0-4.1602-1.8594-4.1602-4.1602zm-37.648-20.52c0-4.3984 3.5781-7.9805 7.9805-7.9805 4.3984 0 7.9805 3.5781 7.9805 7.9805 0 4.3984-3.5781 7.9805-7.9805 7.9805-4.3984 0-7.9805-3.5781-7.9805-7.9805zm-32.531-25.98c-4.6914 0-8.5-3.8086-8.5-8.5s3.8086-8.5 8.5-8.5 8.5 3.8086 8.5 8.5-3.8086 8.5-8.5 8.5zm10.578-4 14.488 4.0391c-0.019531 0.21094-0.03125 0.41016-0.03125 0.62109 0 4.3281 3.5195 7.8516 7.8516 7.8516 4.3281 0 7.8516-3.5195 7.8516-7.8516 0-0.10938-0.011719-0.21875-0.019531-0.32812l10.82-2.5781c0.55078 1.3086 1.3984 2.4492 2.4609 3.3711l-9.0703 14.809c-1.3516-0.58984-2.8398-0.92969-4.3984-0.92969-2.8008 0-5.3594 1.0703-7.3008 2.8008l-23.582-20.066c0.35937-0.55078 0.67187-1.1289 0.92969-1.7383zm22.301-0.19141c2.6719 0 4.8516 2.1797 4.8516 4.8516s-2.1719 4.8516-4.8516 4.8516c-2.6797 0-4.8516-2.1797-4.8516-4.8516s2.1797-4.8516 4.8516-4.8516zm20.93-1.6289c0-3.4102 2.7695-6.1914 6.1914-6.1914s6.1914 2.7695 6.1914 6.1914c0 3.4219-2.7695 6.1914-6.1914 6.1914s-6.1914-2.7695-6.1914-6.1914zm-50.41 30.289c0-2.2891 1.8711-4.1602 4.1602-4.1602 2.2891 0 4.1602 1.8594 4.1602 4.1602s-1.8711 4.1602-4.1602 4.1602-4.1602-1.8594-4.1602-4.1602zm0 34.32c0-6.1797 5.0312-11.211 11.211-11.211s11.211 5.0312 11.211 11.211c0 6.1797-5.0312 11.211-11.211 11.211s-11.211-5.0312-11.211-11.211zm49.559-5.8711c0 1.6406-1.3281 2.9688-2.9688 2.9688s-2.9688-1.3281-2.9688-2.9688 1.3281-2.9688 2.9688-2.9688 2.9688 1.3281 2.9688 2.9688z"/>
            </svg>
          </MobileNavButton>
        </MobileNavGrid>
      </MobileNavSection>

      {/* Pattern-Based Recommendations Section - Full Sidebar */}
      <Section>
        <PatternBasedRecommendations
          userId={userId}
          onSearchRecommendation={onExplore}
          compact={true}
          limit={8}
        />
      </Section>
    </SidebarContainer>
  );
};

export default LeftSidebar;